import authApi from '@/common/api/auth.api';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';
import { useVersionCheck } from '@/composables/useVersionCheck';
import run from '@/userFeatureNotification.js';
import isEmpty from 'lodash/isEmpty';
import { createRouter, createWebHistory } from 'vue-router';
// import NProgress from 'nprogress'; // disabled until we have a real use for this - Ticket #539

// Pages
import InvalidTab from '@/views/InvalidTabPage';
import BasicLayout from '@/views/layouts/BasicLayout.vue';
import DefaultLayout from '@/views/layouts/DefaultLayout.vue';
import NotFound from '@/views/NotFoundPage';

// routes
import authRoutes from '@/routes/auth.js';
import generalLog from '@/routes/generalLog.js';
import reports from '@/routes/reports.js';
import settings from '@/routes/settings.js';
import financialDashboard from './routes/financialDashboard.js';
import lens from '@/routes/lens.js';

export const baseRoutes = [
    {
        path: '/',
        name: 'home',
        component: DefaultLayout,
        redirect: { name: 'dashboard' },
        children: [
            {
                path: 'dashboard/:activeDialog?/:activeId?/:activeGroupId?',
                name: 'dashboard',
                component: () => import('@/views/DashboardPage.vue'),
                props: true,
            },
            {
                path: 'calendar/:activeDialog?/:activeId?/:activeGroupId?',
                name: 'calendar',
                component: () => import('@/views/CalendarPage.vue'),
            },
            {
                path: 'appointment-reminders-report',
                name: 'appointment-reminders-report',
                component: () => import('@/views/reportsLibrary/AppointmentRemindersReportPage.vue'),
            },
            {
                path: 'contacts/:activeMode?/:activeTab?/:activeSubTab?/:activeDialog?/:activeId?/:activeGroupId?',
                name: 'contacts',
                component: () => import('@/views/ContactsPage.vue'),
            },
            {
                path: 'patient/:id/:activeTab?/:activeDialog?/:activeId?/:activeGroupId?',
                name: 'patient',
                component: () => import('@/views/HumanPage.vue'),
                props: true,
            },
            {
                path: 'practitioner/:id/:activeTab?/:activeDialog?/:activeId?/:activeGroupId?',
                name: 'practitioner',
                component: () => import('@/views/HumanPage.vue'),
                props: true,
            },
            {
                path: 'organization/:id/:activeTab?/:activeDialog?/:activeId?/:activeGroupId?',
                name: 'organization',
                component: () => import('@/views/HumanPage.vue'),
                props: true,
            },
            ...settings,
            ...reports,
            ...financialDashboard,
            ...generalLog,
            ...lens,
        ],
    },
    {
        path: '/invalid-tab',
        name: 'invalid-tab',
        meta: { authRequired: false },
        component: InvalidTab,
    },
    {
        path: '/:pathMatch(.*)*',
        name: '404',
        meta: { authRequired: false },
        component: NotFound,
    },
    ...authRoutes,
];

const basePortalRoutes = [
    {
        path: '/auth',
        name: 'auth',
        component: BasicLayout,
        children: [
            {
                path: 'login',
                name: 'login',
                meta: { authRequired: false, isPortal: true },
                component: () => import('@/views/auth/LoginPage.vue'),
                beforeEnter: async (to, from, next) => {
                    let site = window.subdomain;
                    if (!site) {
                        return next();
                    }

                    let account = await authApi.getAccountForLogin(site);

                    if (!account.data?.id) {
                        return next({ name: '404' });
                    }

                    // Props will be passed automatically via the route's props function
                    return next((vm) => {
                        vm.accountDetails = account.data;
                        vm.site = site;
                    });
                },
            },
            {
                path: 'logout',
                name: 'logout',
                meta: { authRequired: false },
                beforeEnter: async (to, from, next) => {
                    const authStore = useAuthStore();
                    await authStore.clearAuth();
                    next({ name: 'login', params: to.params, replace: true });
                },
            },
            {
                path: 'forgot-password',
                name: 'forgot-password',
                meta: { authRequired: false },
                component: () => import('@/views/auth/ForgotPasswordPage.vue'),
            },
            {
                path: 'lockout',
                name: 'lockout',
                meta: { authRequired: false },
                props: true,
                component: () => import('@/views/auth/LockoutPage.vue'),
                beforeEnter: async (to, from, next) => {
                    const authStore = useAuthStore();
                    // if being locked out from the account-selection page, log them out instead.
                    // OR: if no user or account log them out as well.
                    if (from.name === 'account-selection' || !authStore.account || !authStore.user) {
                        return next({ name: 'login' });
                    }

                    // Store the 'from' route in params and proceed
                    to.params.from = from;
                    next();
                },
            },
            {
                path: 'reset-password',
                name: 'reset-password',
                meta: { authRequired: false },
                component: () => import('@/views/auth/ResetPasswordPage.vue'),
            },
            {
                path: 'accept-invitation',
                name: 'accept-invitation',
                meta: { authRequired: false, isPortal: true },
                props: true,
                component: () => import('@/views/auth/AcceptInvitationPage.vue'),
            },
            {
                path: 'confirm-email',
                name: 'confirm-email',
                meta: { authRequired: false },
                props: true,
                component: () => import('@/views/portal/auth/ConfirmEmailPage.vue'),
            },
            {
                path: 'account-selection',
                name: 'account-selection',
                meta: { isPortal: true },
                props: true,
                component: () => import('@/views/auth/AccountSelectionPage.vue'),
            },
        ],
    },
    {
        path: '/',
        name: 'home',
        component: DefaultLayout,
        redirect: { name: 'dashboard' },
        children: [
            {
                path: 'dashboard/:activeDialog?/:activeId?/:activeGroupId?',
                name: 'dashboard',
                component: () => import('@/views/PortalDashboardPage.vue'),
            },
            {
                path: 'calendar',
                name: 'calendar',
                component: () => import('@/views/CalendarPage.vue'),
            },
            {
                path: 'files',
                name: 'files',
                component: () => import('@/views/portal/PortalFilesPage.vue'),
            },
            {
                path: 'resources',
                name: 'resources',
                component: () => import('@/views/portal/PortalResourcesPage.vue'),
            },
            {
                path: 'settings',
                redirect: { name: 'me' },
                name: 'settings',
                component: () => import('@/views/portal/PortalSettingsPage.vue'),
                children: [
                    {
                        path: 'me',
                        name: 'me',
                        component: () => import('@/views/settings/MePage.vue'),
                    },
                ],
            },
        ],
    },
    {
        path: '/applications',
        name: 'portal-applications',
        component: DefaultLayout,
    },
    {
        name: '404',
        path: '/not-found',
        meta: { authRequired: false },
        component: NotFound,
    },
    {
        path: '/:pathMatch(.*)*',
        meta: { authRequired: false },
        component: NotFound,
    },
];

// Helper function to extract subdomain from hostname
function getSubdomain() {
    const hostname = window.location.hostname;

    // Handle localhost for development
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === 'bestnotesfresh.com') {
        // Check for subdomain in port or use a default
        // You can also use a query param or localStorage for dev: ?subdomain=portal
        const urlParams = new URLSearchParams(window.location.search);
        const devSubdomain = urlParams.get('subdomain') || localStorage.getItem('devSubdomain');
        return devSubdomain || 'ehr';
    }

    // Split hostname by dots
    const parts = hostname.split('.');

    // If we have at least 3 parts (e.g., app.example.com), the first is the subdomain
    // If we have 2 parts (e.g., example.com), there's no subdomain (root domain)
    if (parts.length >= 4) {
        return parts[0]; // e.g., 'app', 'portal', 'admin'
    }

    return null;
}

// Get current subdomain
const currentSubdomain = getSubdomain();

// Store subdomain globally for use in components
window.subdomain = currentSubdomain;

// Get routes based on subdomain, fallback to 'ehr' if subdomain not found
let routes = window.page === 'ehr' ? baseRoutes : basePortalRoutes;

let baseUrl = import.meta.env.BASE_URL;
// Only add 'portal' prefix in dev mode for portal subdomain if needed
if (import.meta.env.DEV && window.location.hostname.includes('portal.bestnotesfresh.com')) {
    baseUrl += 'portal';
}

export async function beforeEach(to, from, next) {
    const authStore = useAuthStore();

    // Optional: Validate subdomain and redirect if needed
    const currentSubdomain = getSubdomain();
    const expectedSubdomain = to.meta.subdomain; // You can add this meta property to routes

    // If route requires a specific subdomain and current doesn't match, redirect
    if (expectedSubdomain && currentSubdomain !== expectedSubdomain) {
        // Redirect to correct subdomain
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        let newHostname;

        if (parts.length >= 2) {
            // Replace subdomain: e.g., app.example.com -> portal.example.com
            const baseDomain = parts.slice(-2).join('.'); // Get 'example.com'
            newHostname = `${expectedSubdomain}.${baseDomain}`;
        } else {
            // Fallback for localhost/dev
            newHostname = hostname;
        }

        const newUrl = `${window.location.protocol}//${newHostname}${window.location.port ? ':' + window.location.port : ''}${to.fullPath}`;
        window.location.href = newUrl;
        return;
    }

    // Reset the redirect guard when arriving at login or lockout so future
    // auth failures can redirect again after this navigation completes.
    if (['login', 'lockout'].includes(to.name)) {
        authStore.resetAuthRedirect();
    }

    // Try to refresh credentials in case of page refresh
    if (!authStore.isAuthenticated) {
        await authStore.refreshCredentials();
    }

    // let them logout
    if (to.name === 'logout') {
        return next({ name: 'login' });
    }

    // If user is locked out only let them navigate to the lockout screen, or let them logout.
    if (authStore.isLockedOut && !['lockout', 'login'].includes(to.name)) {
        return next({ name: 'lockout' });
    }

    // Allow navigation to pages that do not require authentication
    if (to.meta.authRequired === false) {
        return next();
    }

    // If we're not authenticated and this page requires authentication redirect to login page
    if (!authStore.isAuthenticated && to.meta.authRequired !== false) {
        return next({ name: 'login' });
    }

    // If we're authenticated and going to dev management let them in
    if (authStore.isAuthenticated && isEmpty(authStore.account) && to.name === 'dev-management') {
        return next();
    }

    // If we're authenticated but no account is selected then redirect to the account selection page
    if (authStore.isAuthenticated && isEmpty(authStore.account) && to.name !== 'account-selection') {
        return next({ name: 'account-selection' });
    }

    // check that user has proper permissions
    if (to.meta.hasPermission && !authStore.hasPermission(to.meta.hasPermission)) {
        useNotificationStore().add({
            message: 'You do not have permission to access this page.',
            color: 'error',
            timeout: 5000,
        });
        // If there's a previous route, go back to it; otherwise go to dashboard
        if (from.name) {
            return next(from);
        }
        return next({ name: 'dashboard' });
    }

    // only allow portal users with no aidbox user to access applications page (until they are accepted)
    // let noUserRoutes = ['account-selection', 'portal-applications'];
    // if (store.state.isPortal && authStore.noAidboxUser && !noUserRoutes.includes(to.name)) {
    //     return next({ name: 'portal-applications' });
    // }

    //Check for the meta tag for showing the progress bar.
    if (to.meta.showProgressBar !== false) {
        // NProgress.start(); // TODO: Make a service that keeps count of the start and done calls and then prevent calling done prematurely
    }
    next();
}

let router = createRouter({
    history: createWebHistory(baseUrl),
    linkActiveClass: 'open active',
    routes: routes,
});

router.beforeEach(async (to, from, next) => beforeEach(to, from, next));

const { updateAvailable, checkForUpdate, reloadForUpdate } = useVersionCheck();

router.afterEach((to) => {
    // NProgress.done();

    // run first time route stuff
    const authStore = useAuthStore();
    run(to, authStore.user.user);

    if (!updateAvailable.value) checkForUpdate();

    if (updateAvailable.value && ['lockout', 'login'].includes(to.name)) {
        reloadForUpdate();
    }
});

window.onload = function () {
    let reloading = sessionStorage.getItem('reloadNotification');
    if (reloading) {
        sessionStorage.removeItem('reloadNotification');
        useNotificationStore().add({
            message: 'An update from the server has been loaded.  Please try again.',
            timeout: 5000,
        });
    }
};

router.onError((error) => {
    if (/loading chunk .* failed|Failed to fetch dynamically imported module/i.test(error.message)) {
        sessionStorage.setItem('reloadNotification', true);
        document.location.reload();
    }
});
export default router;
